import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { usePageEvent, track } from "../lib/tracker";
import {
  CheckCircle2, Sparkles, ArrowRight, Loader2, AlertTriangle, Shield,
  Calendar, ExternalLink, Users, Building2, GraduationCap, Mail,
} from "lucide-react";
import { toast } from "sonner";
import { apiErrorToast } from "../lib/utils";

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

// Individual plan features (unchanged copy — only amounts changed server-side).
const INDIVIDUAL_FEATURES = {
  free:      ["1 Resume Analysis","1 L1 Mock Interview","Basic Feedback"],
  weekly:    ["7-day full access","Unlimited Resume Analysis","Daily Mock Interviews","ATS + Skill Gap reports","Email support"],
  monthly:   ["30-day access","Unlimited Resume Analysis","Unlimited Mock Interviews","ATS + Skill Gap reports","Weekly progress reports","Priority processing"],
  quarterly: ["90-day access","Everything in Monthly","Personalised career roadmap","Premium ATS templates","Recruiter readiness tracking","Save 17% vs monthly"],
  yearly:    ["365-day access","Everything in Quarterly","Full premium templates","Year-long career roadmap","Highest priority queue","Save 48% vs monthly"],
};
const INDIVIDUAL_ORDER = ["free", "weekly", "monthly", "quarterly", "yearly"];

const CORPORATE_FEATURES = [
  "Organization dashboard",
  "Candidate invitations (email + link)",
  "Resume + skill-gap assessments",
  "Side-by-side candidate comparison",
  "Recruitment pipeline tracking",
  "Live license usage reporting",
];
const INSTITUTION_FEATURES = [
  "Institution dashboard",
  "Student invitations (email + link)",
  "Batch & course management",
  "Resume + skill-gap assessments",
  "Individual placement-readiness reports",
  "Batch analytics & license usage",
];

const CATEGORIES = [
  { id: "individual",  label: "Individual",   icon: Users,         sub: "For job seekers" },
  { id: "corporate",   label: "Corporate",    icon: Building2,     sub: "For HR / Talent teams" },
  { id: "institution", label: "Institutions", icon: GraduationCap, sub: "For colleges & placements" },
];

const ENTERPRISE_CONTACT_EMAIL = "sales@skillgappilot.com";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
    if (existing) { existing.addEventListener("load", () => resolve(true)); existing.addEventListener("error", () => resolve(false)); return; }
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT; s.async = true;
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Pricing({ publicView }) {
  usePageEvent("pricing_view");
  const Wrapper = publicView ? PublicWrap : AppWrap;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plansResp, setPlansResp] = useState({ individual: [], corporate: [], institution: [] });
  const [activeSub, setActiveSub] = useState(null);
  const [remainingDays, setRemainingDays] = useState(null);
  const [renewWindowOpen, setRenewWindowOpen] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);  // "weekly" | "corporate:starter" | ...
  const [methods, setMethods] = useState(null);
  const [myOrg, setMyOrg] = useState(null);
  const [category, setCategory] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search).get("category");
      return CATEGORIES.find(c => c.id === q)?.id || "individual";
    } catch { return "individual"; }
  });
  const [orgName, setOrgName] = useState(() => {
    try { return sessionStorage.getItem("sgp_pending_org_name") || ""; } catch { return ""; }
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/payments/plans");
        if (!mounted) return;
        setPlansResp({
          individual: data.individual || data.items || [],
          corporate: data.corporate || [],
          institution: data.institution || [],
        });
      } catch (e) { /* ignore */ }
      try {
        const { data } = await api.get("/payments/methods");
        if (mounted) setMethods(data);
      } catch (e) { /* ignore */ }
      if (user) {
        try {
          const r = await api.get("/payments/my-subscription");
          if (!mounted) return;
          setActiveSub(r.data.active_subscription);
          setRemainingDays(r.data.remaining_days);
          setRenewWindowOpen(!!r.data.renew_window_open);
        } catch (e) { /* ignore */ }
        try {
          const r = await api.get("/org/me");
          if (mounted) setMyOrg(r.data.organization);
          if (mounted && r.data.organization?.name) setOrgName(r.data.organization.name);
        } catch (e) { /* ignore */ }
        loadRazorpayScript();
      }
    })();
    return () => { mounted = false; };
  }, [user]);

  const individualMap = useMemo(() => Object.fromEntries(plansResp.individual.map(p => [p.id, p])), [plansResp]);
  const activeTier = activeSub ? (individualMap[activeSub.plan_id]?.tier_rank ?? 0) : 0;

  const openRazorpay = (order, planLabel, onDone) => {
    const options = {
      key: order.key_id, amount: order.amount, currency: order.currency,
      order_id: order.order_id, name: "SkillGap Pilot",
      description: `${planLabel} plan`,
      theme: { color: "#6366f1" },
      prefill: {
        name: user?.full_name || user?.name || "",
        email: user?.email || "",
        contact: user?.mobile || user?.phone || "",
        method: "upi",
      },
      notes: { plan_id: order.plan_id },
      method: { upi: true, card: true, netbanking: true, wallet: true, paylater: true, emi: false },
      handler: async (response) => {
        try {
          const v = await api.post("/payments/verify", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          if (v.data?.idempotent) toast.info("Payment already processed");
          else toast.success(onDone?.successMsg || "Payment successful! Settlement will finalize in the background.");
          track("payment_success", { plan_id: order.plan_id, amount_inr: Math.round((order.amount || 0) / 100), account_type: order.account_type });
          if (onDone?.redirect) navigate(onDone.redirect(response));
        } catch (e) {
          apiErrorToast(e, "Payment verification failed");
          navigate(`/app/pricing/success?order_id=${response.razorpay_order_id}&failed=1`);
        }
      },
      modal: { ondismiss: () => { setLoadingPlan(null); toast.info("Payment cancelled"); } },
    };
    try {
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed");
        setLoadingPlan(null);
        navigate(`/app/pricing/failed?order_id=${order.order_id}`);
      });
      rzp.open();
      track("checkout_opened", { plan_id: order.plan_id, amount_inr: Math.round((order.amount || 0) / 100), account_type: order.account_type });
    } catch (e) {
      toast.error("Unable to open Razorpay checkout");
      setLoadingPlan(null);
    }
  };

  const startIndividualCheckout = async (planId) => {
    if (planId === "free") { navigate(user ? "/app/dashboard" : "/signup"); return; }
    if (!user) {
      toast.info("Sign in to continue");
      navigate("/login", { state: { from: { pathname: "/app/pricing" } } });
      return;
    }
    setLoadingPlan(planId);
    const ok = await loadRazorpayScript();
    if (!ok || !window.Razorpay) { toast.error("Could not load Razorpay. Check your connection."); setLoadingPlan(null); return; }
    try {
      const { data: order } = await api.post("/payments/checkout", { plan_id: planId });
      openRazorpay(order, order.plan_name, {
        successMsg: "Subscription activated! Settlement will finalize in the background.",
        redirect: (r) => `/app/pricing/success?order_id=${r.razorpay_order_id}`,
      });
    } catch (e) { apiErrorToast(e, "Could not start checkout"); setLoadingPlan(null); }
  };

  const startOrgCheckout = async (planId, accountType) => {
    const label = accountType === "corporate" ? "Corporate" : "Institution";
    const trimmed = (orgName || "").trim();
    if (trimmed.length < 2) {
      toast.error(`Enter your ${label.toLowerCase()} name above the plans to continue.`);
      return;
    }
    if (!user) {
      // Persist the typed org name so it survives the login round-trip.
      try { sessionStorage.setItem("sgp_pending_org_name", trimmed); } catch (_) { /* ignore */ }
      toast.info("Sign in to continue");
      navigate("/login", { state: { from: { pathname: `/app/pricing?category=${accountType}` } } });
      return;
    }
    const key = `${accountType}:${planId}`;
    setLoadingPlan(key);
    const ok = await loadRazorpayScript();
    if (!ok || !window.Razorpay) { toast.error("Could not load Razorpay. Check your connection."); setLoadingPlan(null); return; }
    try {
      const { data: order } = await api.post("/payments/org/checkout", {
        plan_id: planId, account_type: accountType, org_name: trimmed,
      });
      try { sessionStorage.removeItem("sgp_pending_org_name"); } catch (_) {}
      openRazorpay({ ...order, account_type: accountType }, `${label} · ${order.plan_name}`, {
        successMsg: `${label} plan activated! ${order.licenses} licenses credited.`,
        redirect: (r) => `/app/org/dashboard?welcome=1&order_id=${r.razorpay_order_id}`,
      });
    } catch (e) { apiErrorToast(e, "Could not start checkout"); setLoadingPlan(null); }
  };

  const contactSales = (accountType, planName) => {
    const subject = encodeURIComponent(`Enterprise plan enquiry — ${accountType} / ${planName}`);
    const body = encodeURIComponent(
      `Hi SkillGap Pilot sales,\n\n` +
      `I'd like to discuss an Enterprise ${accountType} plan.\n\n` +
      `Organization: \nExpected seats: \nTimeline: \n\nThanks.`,
    );
    window.location.href = `mailto:${ENTERPRISE_CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    track("enterprise_contact_sales", { account_type: accountType });
  };

  // ---- Renderers ----
  const renderIndividualCard = (planId) => {
    const isFree = planId === "free";
    const p = individualMap[planId];
    const features = INDIVIDUAL_FEATURES[planId] || [];
    const highlight = planId === "quarterly";
    const isActive = activeSub?.plan_id === planId;
    const tier = p?.tier_rank ?? 0;
    const isDowngrade = !isFree && activeSub && tier < activeTier;
    const isUpgrade = !isFree && activeSub && tier > activeTier;

    let btnLabel = isFree ? "Start Free" : "Buy Now";
    let disabled = false;
    let stateLabel = "";
    if (isActive) {
      if (renewWindowOpen) { btnLabel = "Renew Now"; stateLabel = `Active · ${remainingDays}d left`; }
      else { btnLabel = "Already Active"; disabled = true; stateLabel = `Active · ${remainingDays}d left`; }
    } else if (isDowngrade) { btnLabel = "Lower than current plan"; disabled = true; }
    else if (isUpgrade) { btnLabel = "Upgrade Now"; }

    const priceLabel = isFree ? "₹0" : (p ? `₹${Number(p.amount).toLocaleString("en-IN")}` : "—");
    const durationLabel = isFree ? "Forever free"
      : p ? (p.duration_days === 7 ? "/week" : p.duration_days === 30 ? "/month" : p.duration_days === 90 ? "/quarter" : "/year")
      : "";

    return (
      <PlanCard key={planId} testid={`plan-${planId}`} highlight={highlight} ribbon={isActive ? "Your Plan" : (highlight ? "Best value" : null)}
        name={p?.name || (isFree ? "Free" : planId)} price={priceLabel} duration={durationLabel} stateLabel={stateLabel}
        features={features} disabled={disabled} loading={loadingPlan === planId} btnLabel={btnLabel}
        onCta={() => startIndividualCheckout(planId)}
        footerNote={!isFree ? "Secure checkout · UPI · Cards · Net banking" : undefined}
        ctaTestid={`plan-cta-${planId}`}
      />
    );
  };

  const renderOrgCard = (p, accountType) => {
    const isEnterprise = !!p.contact_sales;
    const highlight = p.id === "growth";
    const key = `${accountType}:${p.id}`;
    const myPlanActive = myOrg && myOrg.account_type === accountType && myOrg.plan_id === p.id && myOrg.status === "active";
    const myOtherOrgActive = myOrg && myOrg.status === "active" && !myPlanActive;

    const priceLabel = isEnterprise
      ? `₹${Number(p.amount).toLocaleString("en-IN")}+`
      : `₹${Number(p.amount).toLocaleString("en-IN")}`;
    const subhead = isEnterprise ? `${p.licenses}+ licenses · 12 months`
                                 : `${p.licenses} licenses · 12 months`;

    let btnLabel = isEnterprise ? "Contact Sales" : "Buy Now";
    let disabled = false;
    let ribbon = null;
    if (myPlanActive) {
      btnLabel = "Already Active"; disabled = true; ribbon = "Your Plan";
    } else if (myOtherOrgActive && !isEnterprise) {
      btnLabel = "Contact Sales to add seats"; disabled = true;
    } else if (highlight) {
      ribbon = "Most popular";
    }

    return (
      <PlanCard key={key} testid={`plan-${accountType}-${p.id}`} highlight={highlight} ribbon={ribbon}
        name={p.name} price={priceLabel} duration={subhead}
        features={accountType === "corporate" ? CORPORATE_FEATURES : INSTITUTION_FEATURES}
        disabled={disabled} loading={loadingPlan === key} btnLabel={btnLabel}
        onCta={() => isEnterprise ? contactSales(accountType, p.name) : startOrgCheckout(p.id, accountType)}
        footerNote={isEnterprise ? `We'll reply to ${ENTERPRISE_CONTACT_EMAIL} within 1 business day.` : "Bulk license · GST invoice · 12-month validity"}
        ctaIcon={isEnterprise ? <Mail size={14}/> : null}
        ctaTestid={`plan-cta-${accountType}-${p.id}`}
      />
    );
  };

  return (
    <Wrapper>
      {user?.is_admin && methods && methods.key_mode === "test" && (
        <div className="mb-6 rounded-md border border-red-500 bg-red-50 px-4 py-3 flex items-start gap-3" data-testid="test-keys-in-prod-banner">
          <AlertTriangle size={16} className="text-red-600 mt-0.5" />
          <div className="text-sm flex-1">
            <div className="font-semibold text-red-900">Razorpay is running with TEST keys — buyers cannot pay</div>
            <div className="text-red-800 mt-1">
              Current key id: <code className="font-mono text-xs bg-red-100 px-1 rounded">{methods.key_id}</code>. Test keys (<code>rzp_test_*</code>) are blocked in production. Fix: open <strong>Emergent Deploy → Environment Variables</strong>, set <code>RAZORPAY_KEY_ID</code> to your live key (starts with <code>rzp_live_</code>) and <code>RAZORPAY_KEY_SECRET</code> to the matching secret, then click <strong>Redeploy</strong>.
            </div>
          </div>
        </div>
      )}
      {activeSub && category === "individual" && (
        <div className="mb-6 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-3" data-testid="active-sub-banner">
          <Shield size={16} className="text-emerald-700 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-emerald-800">You already have an active subscription.</div>
            <div className="text-emerald-700">Your <strong>{activeSub.plan_name}</strong> plan expires on <strong>{(activeSub.expiry_date || "").slice(0,10)}</strong> ({remainingDays} days remaining). {renewWindowOpen ? "Renewal window is open." : "Renewal opens in the last days before expiry — or upgrade to a higher tier anytime."}</div>
          </div>
        </div>
      )}
      {myOrg && (category === "corporate" || category === "institution") && (
        <div className="mb-6 rounded-md border border-sky-200 bg-sky-50 px-4 py-3 flex items-start justify-between gap-3" data-testid="active-org-banner">
          <div className="flex items-start gap-3">
            <Shield size={16} className="text-sky-700 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-sky-900">You have an active <strong>{myOrg.plan_name}</strong> {myOrg.account_type} plan.</div>
              <div className="text-sky-800">{myOrg.licenses_total} licenses · valid until <strong>{(myOrg.expiry_date || "").slice(0,10)}</strong>.</div>
            </div>
          </div>
          <Link to="/app/org/dashboard" data-testid="goto-org-dashboard" className="shrink-0 text-sm font-semibold px-3 py-1.5 rounded-md bg-sky-600 text-white hover:bg-sky-700 inline-flex items-center gap-1">
            Open Dashboard <ArrowRight size={14} />
          </Link>
        </div>
      )}
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Pricing · SkillGap Pilot</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2 text-balance">
          Pick the plan that fits <span className="rainbow-text">your team</span>.
        </h1>
        <p className="text-zinc-600 mt-3 text-sm max-w-2xl">
          Individuals pay by UPI, cards or net banking. Corporate & Institution plans credit bulk licenses valid for 12 months.
        </p>
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 mb-6 flex-wrap" role="tablist" data-testid="pricing-tabs">
        {CATEGORIES.map(c => {
          const Icon = c.icon;
          const active = category === c.id;
          return (
            <button key={c.id} data-testid={`pricing-tab-${c.id}`}
              role="tab" aria-selected={active}
              onClick={() => setCategory(c.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition border ${active ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50'}`}>
              <Icon size={14}/> <span>{c.label}</span>
              <span className={`hidden md:inline text-[11px] font-normal ${active ? 'text-white/70' : 'text-zinc-500'}`}>· {c.sub}</span>
            </button>
          );
        })}
      </div>

      {/* Content per category */}
      {category === "individual" && (
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="plans-individual">
          {INDIVIDUAL_ORDER.map(renderIndividualCard)}
        </div>
      )}

      {(category === "corporate" || category === "institution") && (
        <div className="space-y-4" data-testid={`plans-${category}`}>
          <div className="rounded-lg border border-zinc-200 bg-white p-4 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="org-name-row">
            <div className="flex-1">
              <label htmlFor="org-name-input" className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                {category === "corporate" ? "Company name" : "Institution name"}
              </label>
              <input id="org-name-input" value={orgName} onChange={e => setOrgName(e.target.value)}
                data-testid="org-name-input"
                disabled={!!myOrg}
                placeholder={category === "corporate" ? "e.g. Acme Technologies Pvt Ltd" : "e.g. ABC Institute of Technology"}
                className="mt-1 w-full h-10 px-3 border border-zinc-300 rounded-md bg-white text-sm disabled:bg-zinc-50 disabled:text-zinc-500"/>
            </div>
            <div className="text-xs text-zinc-500 sm:max-w-xs">
              Appears on your invoice and on invitation emails to {category === "corporate" ? "candidates" : "students"}. You can edit it later from the Org Dashboard.
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {plansResp[category].map(p => renderOrgCard(p, category))}
          </div>
          <div className="rounded-md bg-zinc-50 border border-zinc-200 p-4 text-xs text-zinc-600" data-testid="org-faq">
            <div className="font-semibold text-zinc-800 mb-1">How bulk licenses work</div>
            <ul className="space-y-1 list-disc pl-5">
              <li>One license = one seat for one {category === "corporate" ? "candidate" : "student"} for 12 months from purchase.</li>
              <li>Admin invites by email or shareable link; the license is reserved on invite and consumed when the person signs up.</li>
              <li>You can revoke a pending invite anytime and the license goes back to the pool.</li>
              <li>Unused licenses expire 12 months from purchase — set a reminder on your Org Dashboard.</li>
            </ul>
          </div>
        </div>
      )}

      <div className="mt-8 text-xs text-zinc-500 flex items-center gap-2">
        <Calendar size={12} /> Subscription dates, payment history & invoices are available on your{" "}
        {category === "individual"
          ? <Link to="/app/dashboard" className="text-indigo-600 font-semibold">Dashboard</Link>
          : <Link to="/app/org/dashboard" className="text-indigo-600 font-semibold">Org Dashboard</Link>
        }.
      </div>
    </Wrapper>
  );
}

function PlanCard({ testid, highlight, ribbon, name, price, duration, stateLabel, features, disabled, loading, btnLabel, onCta, footerNote, ctaIcon, ctaTestid }) {
  return (
    <div
      className={`relative p-6 rounded-xl border flex flex-col ${highlight ? 'bg-gradient-to-br from-indigo-600 via-sky-500 to-emerald-500 text-white border-transparent shadow-xl' : 'bg-white border-zinc-200'}`}
      data-testid={testid}
    >
      {ribbon && (
        <div className={`absolute -top-3 ${ribbon === "Your Plan" ? "right-6 bg-emerald-500" : "left-6 bg-black"} text-[10px] tracking-[0.25em] uppercase font-bold text-white px-2 py-1 rounded`}>
          {ribbon}
        </div>
      )}
      <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">{name}</div>
      <div className="mt-3 flex items-baseline gap-1">
        <div className="font-display font-black text-4xl">{price}</div>
        <div className="text-sm opacity-70">{duration}</div>
      </div>
      {stateLabel && <div className={`text-[11px] mt-1 ${highlight ? 'text-white/90' : 'text-emerald-700'}`}>{stateLabel}</div>}
      <ul className="mt-5 space-y-2 text-sm flex-1">
        {features.map(f => (
          <li key={f} className="flex items-start gap-2">
            <CheckCircle2 size={14} className={`mt-1 ${highlight ? 'text-white' : 'text-emerald-600'}`} />
            <span className={highlight ? 'opacity-95' : 'text-zinc-700'}>{f}</span>
          </li>
        ))}
      </ul>
      <button
        data-testid={ctaTestid}
        onClick={onCta}
        disabled={loading || disabled}
        className={`mt-6 inline-flex items-center justify-center w-full px-4 py-2.5 rounded-md font-semibold text-sm gap-1 transition disabled:opacity-50 disabled:cursor-not-allowed ${highlight ? 'bg-white text-zinc-900 hover:bg-zinc-100' : 'bg-gradient-to-r from-indigo-500 to-sky-500 text-white hover:opacity-90'}`}
      >
        {loading ? <><Loader2 size={14} className="animate-spin" /> Opening Razorpay…</>
                 : <>{ctaIcon}{btnLabel} {!disabled && !ctaIcon && <ArrowRight size={14} />}</>}
      </button>
      {footerNote && <p className={`text-xs mt-3 ${highlight ? 'opacity-70' : 'text-zinc-500'}`}>{footerNote}</p>}
    </div>
  );
}

function PublicWrap({ children }) {
  return (
    <div className="min-h-screen bg-aurora text-zinc-900">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 bg-gradient-to-br from-indigo-500 to-sky-500 text-white rounded-md flex items-center justify-center"><Sparkles size={16} /></div>
            <span className="font-display font-black text-lg">SkillGap <span className="rainbow-text">Pilot</span></span>
          </Link>
          <Link to="/signup" className="text-sm font-semibold px-4 py-2 rounded-md bg-gradient-to-r from-indigo-500 to-sky-500 text-white hover:opacity-90">Start Free</Link>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-6 py-16">{children}</div>
    </div>
  );
}

function AppWrap({ children }) {
  return <div className="p-6 md:p-10">{children}</div>;
}
